import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Switch, Divider, Chip,
  TableBody, TableRow, TableCell,
  TablePagination, CircularProgress, Alert,
  TextField, FormControlLabel, Button,
  Select, MenuItem, FormControl, InputLabel, Stack,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import whatsappService from '../services/whatsappService';
import useSnackbar from '../hooks/useSnackbar';
import PageLayout from '../components/ui/PageLayout';
import StandardCard from '../components/ui/StandardCard';
import StandardTable from '../components/ui/StandardTable';
import AppSnackbar from '../components/ui/AppSnackbar';
import EmptyState from '../components/ui/EmptyState';
import ProductCatalog from '../components/billing/ProductCatalog';
import { useMeter } from '../hooks/useEntitlements';

const DIRECTION_LABEL = { outbound: 'Enviada', inbound: 'Recebida' };
const PURPOSE_LABEL = {
  created: 'Agendamento criado',
  confirmation_request: 'Confirmação',
  reminder: 'Lembrete',
  cancelled: 'Cancelamento',
  reply: 'Resposta',
};

const STATUS_COLOR = {
  sent:        'info',
  delivered:   'success',
  read:        'success',
  failed:      'error',
  undelivered: 'error',
  received:    'default',
};

// Modelo de mensagem (espelha o int template_style no backend): 1, 2, … (o dono escolhe).

const DEFAULT_SETTINGS = {
  enabled: false,
  templateStyle: 2,
  messageNotes: '',
  notifyOnPublicCreate: true,
  notifyOnPrivateCreate: true,
  reminderEnabled: false,
  reminderHoursBefore: 24,
  reminderRespectBusinessHours: true,
  reminderBusinessStart: 8,
  reminderBusinessEnd: 18,
  // Notificações pro DONO (Fase 5)
  ownerNotifyEnabled: false,
  ownerPhone: '',
  ownerNotifyOnCreate: true,
  ownerNotifyOnConfirm: true,
  ownerNotifyOnCancel: true,
};

const messageColumns = [
  { id: 'createdAt',   label: 'Data/Hora' },
  { id: 'direction',   label: 'Direção' },
  { id: 'purpose',     label: 'Tipo' },
  { id: 'status',      label: 'Status', align: 'center' },
  { id: 'phoneMasked', label: 'Telefone' },
];

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function clampHour(v, fallback) {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(24, Math.max(0, n));
}

export default function IntegrationsPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const { balance: waBalance, low: waLow } = useMeter('whatsapp_messages'); // saldo de mensagens (5.5)

  // ── Settings state ──────────────────────────────────────────────────────────
  const [form, setForm]                       = useState(DEFAULT_SETTINGS);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings]   = useState(false);

  // ── Messages state ──────────────────────────────────────────────────────────
  const [messages, setMessages]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);          // 0-indexed (TablePagination)
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [summary, setSummary]     = useState(null);   // resumo de gasto (5.6)

  // ── Load settings on mount ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const data = await whatsappService.getSettings();
        setForm({ ...DEFAULT_SETTINGS, ...data, messageNotes: data.messageNotes ?? '', ownerPhone: data.ownerPhone ?? '' });
      } catch {
        // Silencioso — mantém defaults
      } finally {
        setLoadingSettings(false);
      }
    })();
  }, []);

  // ── Load messages when page/rowsPerPage change ──────────────────────────────
  const loadMessages = useCallback(async (p, rpp) => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const data = await whatsappService.listMessages(p + 1, rpp); // API é 1-indexed
      setMessages(data.items ?? []);
      setTotal(data.total ?? 0);
      setSummary(data.summary ?? null);
    } catch {
      setMessagesError('Não foi possível carregar o histórico. Tente novamente.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadMessages(page, rowsPerPage);
  }, [page, rowsPerPage, loadMessages]);

  // ── Form helpers ────────────────────────────────────────────────────────────
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    setSavingSettings(true);
    try {
      await whatsappService.updateSettings({
        ...form,
        messageNotes: form.messageNotes?.trim() ? form.messageNotes.trim() : null,
        reminderHoursBefore: Math.min(168, Math.max(1, parseInt(form.reminderHoursBefore, 10) || 24)),
        reminderBusinessStart: clampHour(form.reminderBusinessStart, 8),
        reminderBusinessEnd: clampHour(form.reminderBusinessEnd, 18),
      });
      showSuccess('Configurações de WhatsApp salvas.');
    } catch {
      showError('Não foi possível salvar as configurações. Tente novamente.');
    } finally {
      setSavingSettings(false);
    }
  }, [form, showSuccess, showError]);

  // ── Pagination handlers ─────────────────────────────────────────────────────
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const bStart = clampHour(form.reminderBusinessStart, 8);
  const bEnd = clampHour(form.reminderBusinessEnd, 18);

  return (
    <PageLayout
      title="Integrações"
      subtitle="Gerencie suas integrações externas"
      icon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
    >
      {/* ── Card WhatsApp ─────────────────────────────────────────────────── */}
      <StandardCard sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
          <Typography variant="h6">WhatsApp</Typography>
          {loadingSettings ? (
            <CircularProgress size={16} sx={{ ml: 'auto' }} />
          ) : (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {form.enabled ? 'Habilitado' : 'Desabilitado'}
              </Typography>
              <Switch
                checked={form.enabled}
                onChange={(e) => updateField('enabled', e.target.checked)}
                color="success"
              />
            </Box>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary">
          Quando habilitado, seus clientes recebem mensagens de agendamento pelo WhatsApp.
          Configure abaixo o estilo, as orientações e os lembretes.
        </Typography>

        {!loadingSettings && form.enabled && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 3 }} />

            {/* Estilo da mensagem */}
            <FormControl size="small" sx={{ minWidth: 240, mb: 3 }}>
              <InputLabel id="wa-style-label">Modelo da mensagem</InputLabel>
              <Select
                labelId="wa-style-label"
                label="Modelo da mensagem"
                value={form.templateStyle}
                onChange={(e) => updateField('templateStyle', Number(e.target.value))}
              >
                <MenuItem value={1}>Modelo 1</MenuItem>
                <MenuItem value={2}>Modelo 2</MenuItem>
              </Select>
            </FormControl>

            {/* Orientações globais */}
            <TextField
              label="Orientações ao cliente (opcional)"
              value={form.messageNotes}
              onChange={(e) => updateField('messageNotes', e.target.value)}
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
              inputProps={{ maxLength: 600 }}
              helperText="Aparece em todas as mensagens. Ex.: vir em jejum, trazer documento, chegar 10min antes."
              sx={{ mb: 3 }}
            />

            <Divider sx={{ mb: 2 }} />

            {/* Notificar na criação */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Avisar o cliente ao criar o agendamento
            </Typography>
            <Stack sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.notifyOnPublicCreate}
                    onChange={(e) => updateField('notifyOnPublicCreate', e.target.checked)}
                    color="success"
                  />
                }
                label="Quando o agendamento vem do site público"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.notifyOnPrivateCreate}
                    onChange={(e) => updateField('notifyOnPrivateCreate', e.target.checked)}
                    color="success"
                  />
                }
                label="Quando você cria o agendamento aqui no painel"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              Se desligar, não enviamos a mensagem de criação — apenas o lembrete (se ativo).
              Ao criar um agendamento no painel você ainda pode forçar o envio caso a caso.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Lembrete */}
            <FormControlLabel
              control={
                <Switch
                  checked={form.reminderEnabled}
                  onChange={(e) => updateField('reminderEnabled', e.target.checked)}
                  color="success"
                />
              }
              label="Enviar lembrete antes do agendamento"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              O lembrete consome cota de mensagens e traz os botões Confirmar / Cancelar.
            </Typography>

            {form.reminderEnabled && (
              <Box sx={{ pl: { xs: 0, sm: 2 } }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Horas antes"
                    type="number"
                    size="small"
                    value={form.reminderHoursBefore}
                    onChange={(e) => updateField('reminderHoursBefore', e.target.value)}
                    inputProps={{ min: 1, max: 168 }}
                    sx={{ width: 130 }}
                  />
                </Stack>

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.reminderRespectBusinessHours}
                      onChange={(e) => updateField('reminderRespectBusinessHours', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Respeitar horário comercial"
                />

                {form.reminderRespectBusinessHours && (
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="Início"
                      type="number"
                      size="small"
                      value={form.reminderBusinessStart}
                      onChange={(e) => updateField('reminderBusinessStart', e.target.value)}
                      inputProps={{ min: 0, max: 24 }}
                      sx={{ width: 110 }}
                    />
                    <Typography variant="body2" color="text.secondary">às</Typography>
                    <TextField
                      label="Fim"
                      type="number"
                      size="small"
                      value={form.reminderBusinessEnd}
                      onChange={(e) => updateField('reminderBusinessEnd', e.target.value)}
                      inputProps={{ min: 0, max: 24 }}
                      sx={{ width: 110 }}
                    />
                  </Stack>
                )}

                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Se o horário do lembrete cair fora do expediente, ele é deslocado para dentro dele.
                    Exemplos (expediente {bStart}h–{bEnd}h):
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>Agendamento às 14h, lembrete 3h antes → 11h → enviado <b>11h do mesmo dia</b>.</li>
                    <li>Agendamento ao meio-dia, lembrete 12h antes → 00h → enviado <b>{bEnd}h do dia anterior</b>.</li>
                    <li>Agendamento às 20h, lembrete 1h antes → 19h → enviado <b>{bEnd}h do mesmo dia</b>.</li>
                  </Box>
                </Alert>
              </Box>
            )}
            {/* ── Notificações pra você (dono) — Fase 5 ───────────────────── */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Notificações pra você (dono)</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.ownerNotifyEnabled}
                  onChange={(e) => updateField('ownerNotifyEnabled', e.target.checked)}
                  color="success"
                />
              }
              label="Receber notificações no meu WhatsApp"
            />
            {form.ownerNotifyEnabled && (
              <Box sx={{ pl: { xs: 0, sm: 2 }, mt: 1 }}>
                <TextField
                  label="Meu WhatsApp (com DDD)"
                  value={form.ownerPhone}
                  onChange={(e) => updateField('ownerPhone', e.target.value)}
                  placeholder="(31) 99999-9999"
                  inputProps={{ inputMode: 'tel', maxLength: 20 }}
                  sx={{ mb: 2, maxWidth: 280 }}
                  fullWidth
                />
                <Stack>
                  <FormControlLabel
                    control={<Switch checked={form.ownerNotifyOnCreate} onChange={(e) => updateField('ownerNotifyOnCreate', e.target.checked)} color="success" />}
                    label="Novo agendamento"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.ownerNotifyOnConfirm} onChange={(e) => updateField('ownerNotifyOnConfirm', e.target.checked)} color="success" />}
                    label="Cliente confirmou presença"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.ownerNotifyOnCancel} onChange={(e) => updateField('ownerNotifyOnCancel', e.target.checked)} color="success" />}
                    label="Cliente cancelou/desistiu"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Cada notificação enviada ao seu WhatsApp consome 1 crédito de mensagem.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {!loadingSettings && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              disabled={savingSettings}
              startIcon={savingSettings ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Salvar configurações
            </Button>
          </Box>
        )}
      </StandardCard>

      {/* ── Alerta de saldo (5.5) — gatilho de venda quando baixo/zerado ─── */}
      {form.enabled && (waBalance === 0 || waLow) && (
        <Alert severity={waBalance === 0 ? 'error' : 'warning'} sx={{ mb: 3 }}>
          {waBalance === 0
            ? 'Você está sem saldo de mensagens. Compre um pacote abaixo para voltar a enviar pelo WhatsApp.'
            : `Seu saldo de mensagens está baixo (${waBalance}). Garanta um pacote abaixo para não ficar sem.`}
        </Alert>
      )}

      {/* ── Vitrine de pacotes (PIX embeddado) ───────────────────────────── */}
      <ProductCatalog meter="whatsapp_messages" />

      {/* ── Histórico ────────────────────────────────────────────────────── */}
      <StandardCard>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Histórico de Mensagens
        </Typography>

        {summary && total > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Chip size="small" color="success" variant="outlined" label={`${summary.sent} enviadas · ~${summary.sent} créditos`} />
            {summary.failedNoCredit > 0 && (
              <Chip size="small" color="error" variant="outlined" label={`${summary.failedNoCredit} sem saldo`} />
            )}
            <Chip size="small" variant="outlined" label={`${summary.received} recebidas`} />
          </Stack>
        )}

        <Divider sx={{ mb: 2 }} />

        {messagesError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessagesError(null)}>
            {messagesError}
          </Alert>
        )}

        {!loadingMessages && total === 0 && !messagesError ? (
          <EmptyState
            icon={WhatsAppIcon}
            title="Nenhuma mensagem enviada"
            message="Quando você habilitar o WhatsApp, o histórico de mensagens aparecerá aqui."
          />
        ) : (
          <>
            <StandardTable
              columns={messageColumns}
              loading={loadingMessages}
              isEmpty={messages.length === 0 && !loadingMessages}
              emptyMessage="Nenhuma mensagem nesta página."
            >
              <TableBody>
                {messages.map((msg, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{formatDateTime(msg.createdAt)}</TableCell>
                    <TableCell>
                      {DIRECTION_LABEL[msg.direction] ?? msg.direction}
                    </TableCell>
                    <TableCell>
                      {PURPOSE_LABEL[msg.purpose] ?? msg.purpose ?? '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={msg.status}
                        color={STATUS_COLOR[msg.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                      {msg.phoneMasked}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StandardTable>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </>
        )}
      </StandardCard>

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
